  import React, { useCallback, useEffect, useRef, useState } from 'react';
import videojs from 'video.js';
import { useAuthContext } from '../../../context/AuthContext';
import { ConvertToCloudFrontUrl } from '../../../utils/ConvertToCloudFrontUrl';
import { PUBLISH_USER } from '../../../graphql-client/Mutation';
import { URL_VIDEO_PLAYING } from '../../../graphql-client/Queries';
import { useMutation, useQuery } from '@apollo/client';
import moment from 'moment';

export default function WatchingVideoLeft(props) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const sessionWatchTime = useRef(0); // Tổng thời gian thực tế người dùng đã xem trong phiên
  const lastVideoTime = useRef(0); // Vị trí video lần cuối cùng
  const pendingWatchTime = useRef(0); // Thời gian chờ gửi lên server
  const [watchedTime, setWatchedTime] = useState(0); 
  const {isAuthenticated} = useAuthContext() 
  
  const { loading, error, data } = useQuery(URL_VIDEO_PLAYING, {
    variables: { id: props.id },
    skip: !props.id,
  });

  // cập nhật thời gian xem gói tháng, thiếu cập nhật gói năm
  const [updateMonthlyWatchTime] = useMutation(PUBLISH_USER);
  
  const urlVideoConverted = ConvertToCloudFrontUrl(data?.video?.url);
  const canWatchVideo = isAuthenticated && props?.isMonthlyWatchTime > 0;

  // =============== COMMIT THỜI GIAN XEM LÊN SERVER ===============
  const commitWatchTime = useCallback(() => {
    if (pendingWatchTime.current > 0) {
      updateMonthlyWatchTime({
        variables: { monthlyWatchTime: Math.round(pendingWatchTime.current) }
      });
      pendingWatchTime.current = 0;
    }
  }, [updateMonthlyWatchTime]);

  useEffect(() => {
    // =============== RESET KHI CHUYỂN VIDEO ===============
    setWatchedTime(0);
    sessionWatchTime.current = 0;
    pendingWatchTime.current = 0;
    lastVideoTime.current = 0;

    const existingPlayer = playerRef.current;

    // Không gọi off('play'|'pause'|...) vì sẽ gỡ luôn listener nội bộ của video.js
    if (existingPlayer && !existingPlayer.isDisposed()) {
      try {
        commitWatchTime();
        existingPlayer.dispose();
      } catch (error) {
        console.error('Error disposing player:', error);
      } finally {
        playerRef.current = null;
      }
    }

    if (!containerRef.current || !canWatchVideo) {
      return undefined;
    }

    // =============== TẠO PLAYER MỚI ===============
    // video.js sẽ tự bọc và thay đổi DOM của thẻ video, nên React chỉ giữ container.
    const videoContainer = containerRef.current;
    const videoElement = document.createElement('video');
    videoElement.className = 'video-js vjs-default-skin videoPlay mx-auto mt-2 p-2';
    videoContainer.replaceChildren(videoElement);

    const player = videojs(videoElement, {
      controls: true,
      autoplay: false,
      preload: 'metadata',
      playbackRates: [0.5, 1, 1.5, 2],
    });

    playerRef.current = player;

    player.src({
      src: urlVideoConverted,
      type: 'video/mp4',
    });

    const handlePlay = () => {
      if (!player.isDisposed()) {
        lastVideoTime.current = player.currentTime();
      }
    };

    const handleTimeUpdate = () => {
      if (player.isDisposed()) return;

      const currentTime = player.currentTime();
      const delta = currentTime - lastVideoTime.current;

      // Chỉ tính thời gian khi video tiến về phía trước (không tính khi tua lùi)
      if (delta > 0 && delta < 2) {
        const playbackRate = player.playbackRate() || 1;
        const actualWatched = delta * playbackRate;

        sessionWatchTime.current += actualWatched;
        pendingWatchTime.current += actualWatched;
        setWatchedTime(sessionWatchTime.current);
      }

      lastVideoTime.current = currentTime;
    };

    const handleSeeking = () => {
      if (!player.isDisposed()) {
        lastVideoTime.current = player.currentTime();
      }
    };

    player.on('play', handlePlay);
    player.on('timeupdate', handleTimeUpdate);
    player.on('pause', commitWatchTime);
    player.on('ended', commitWatchTime);
    player.on('dispose', commitWatchTime);
    player.on('seeking', handleSeeking);

    return () => {
      try {
        commitWatchTime();

        if (!player.isDisposed()) {
          player.dispose();
        }

        // Chỉ xóa node nếu nó vẫn còn thuộc container để tránh removeChild lỗi khi video.js đã dọn trước.
        if (videoContainer.contains(videoElement)) {
          videoContainer.removeChild(videoElement);
        }
      } catch (error) {
        console.error('Error in cleanup:', error);
      } finally {
        if (playerRef.current === player) {
          playerRef.current = null;
        }
      }
    };
  }, [canWatchVideo, commitWatchTime, urlVideoConverted]);


  // =============== XỬ LÝ CHUYỂN TAB VÀ THOÁT TRANG ===============
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        commitWatchTime(); // Commit khi chuyển tab
      }
    };
  
    const handleBeforeUnload = () => {
      commitWatchTime(); // Commit khi thoát trang
    };
  
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);
  
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [commitWatchTime]);

  // =============== FORMAT THỜI GIAN HIỂN THỊ ===============
  const formatDurationText = (seconds) => {
    const duration = moment.duration(seconds, 'seconds');
    const hours = duration.hours();
    const minutes = duration.minutes();
    const secs = duration.seconds();

    if (hours > 0) {
        return `${hours} giờ ${minutes} phút ${secs} giây`;
    } else if (minutes > 0) {
        return `${minutes} phút ${secs} giây`;
    } else {
        return `${secs} giây`;
    }
  };

  if (loading) return <p>loading...</p>;
  if (error) return <p>Error! {error.message}</p>;

  return (
    <>
      {canWatchVideo
        ?
        <>
          {urlVideoConverted === '/default-avatar.png' ?
          <div className="alert alert-dark text-center mt-3 p-3">
            Không có video
          </div>
          :(
          <>
            <div ref={containerRef} className="d-flex justify-content-center w-100"></div>
            <div className="col-11 mx-auto">
              <div className="alert alert-success p-2 m-1" role="alert">
                Thời lượng đã xem trong phiên xem: {formatDurationText(watchedTime)}
                <br />
                Nếu sảy ra tình trạng giật lag hãy dừng video lại vài giây rồi tiếp tục phát video nhé!
              </div>
            </div>
          </>)}
        </>
        : null}
    </>
  );
}